// Edición de una lectura de recibo (lo que leyó la IA) en una tabla. Lógica pura y probada: de la tabla editada a las
// ediciones que se mandan (JSON pointer + antes + después). Si solo cambiaron celdas, una edición por celda (el
// historial queda legible: "JUL 26 · consumo: 3,650 → 3,800"); si se agregaron o quitaron periodos, se reemplaza el
// historial completo. Una celda que el vendedor cambió queda con origen "manual" (la IA no la pisa al volver a leer).
import { MOTIVO_EDITADO_A_MANO, TARIFAS, escritoSinRecibo, iguales, lecturaDeRecibos, periodoConConsumoEditado, vieneDelRecibo } from '@cotizador/compartido';
import type { DatosPrivados, EdicionCampo, Facturacion, LecturaRecibo, Medidor, PeriodoCalculo, PeriodoConsumo, Tarifa } from '@cotizador/compartido';
import { numeroDe } from './cuestionario.ts';
import { reemplazo } from './ediciones.ts';

/** ¿Esta lectura ya trae datos? El servidor crea el borrador con una lectura "sin_leer" y el historial vacío. */
export function lecturaConDatos(l: LecturaRecibo | null | undefined): l is LecturaRecibo {
  return Boolean(l) && (l!.historial.length > 0 || l!.estado !== 'sin_leer');
}

export interface FilaEditable {
  /** Índice en el historial guardado (null = periodo nuevo). */
  idx: number | null;
  etiqueta: string;
  inicio: string;
  fin: string;
  kwh: string;
  importe: string;
  demanda: string;
  /** ADENDA z60: lo puso «Completar el año» (todavía sin guardar): con estos kWh se guarda como «estimado» (si el vendedor
   *  escribe otro número, ya es suyo: «a mano»). */
  estimado?: { motivo: string; kwh: number; /** hoy86 · z64-2: lo sugirió la IA (no la curva): se guarda como `sugerido_ia` */ ia?: 'ia' | 'ia_simulada' };
  /** ADENDA z60: el primer periodo del alta, que «Completar el año» tomó como el periodo completo (cambió su inicio): conserva
   *  su origen (sus kWh siguen siendo del recibo). */
  redatada?: true;
}

export interface FormaLectura {
  tarifa: Tarifa | '';
  facturacion: Facturacion | '';
  total: string;
  demandaContratada: string;
  cargaConectada: string;
  filas: FilaEditable[];
  privados: Record<keyof DatosPrivados, string>;
}

const s = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));

export function formaDeLectura(l: LecturaRecibo): FormaLectura {
  return {
    tarifa: l.tarifa ?? '',
    facturacion: l.facturacion ?? '',
    total: s(l.total_a_pagar),
    demandaContratada: s(l.demanda_contratada_kw),
    cargaConectada: s(l.carga_conectada_kw),
    filas: l.historial.map((p, i) => ({ idx: i, etiqueta: p.etiqueta, inicio: p.inicio ?? '', fin: p.fin ?? '', kwh: s(p.consumo_kwh), importe: s(p.importe), demanda: s(p.demanda_kw) })),
    privados: {
      titular: l.privados.titular ?? '',
      numero_servicio: l.privados.numero_servicio ?? '',
      direccion: l.privados.direccion ?? '',
      rmu: l.privados.rmu ?? '',
      cuenta: l.privados.cuenta ?? '',
      numero_medidor: l.privados.numero_medidor ?? '',
    },
  };
}

export type ErroresLectura = Record<string, string>;

function num(v: string, clave: string, errores: ErroresLectura, requerido: boolean): number | null {
  const n = numeroDe(v);
  if (n === null) {
    if (requerido) errores[clave] = 'Falta';
    return null;
  }
  if (!Number.isFinite(n) || n < 0) {
    errores[clave] = 'Número no válido';
    return null;
  }
  return n;
}

const FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** ADENDA z61: una fila NUEVA (la puso el cotizador: un periodo del año sin recibo) que todavía no trae kWh. No se guarda (vuelve
 *  a salir al abrir la tabla mientras no esté autorizada) y no deja autorizar: «Faltan N meses: escríbelos o usa Sugerir con IA». */
export const filaEnBlanco = (f: FilaEditable): boolean => f.idx === null && f.kwh.trim() === '';

/** Ediciones que hay que mandar para que el servidor quede como la forma. Si hay errores, ediciones vacía. ADENDA z61: las filas
 *  EN BLANCO no se mandan (`enBlanco` dice cuántas hay: con alguna, la tabla no se autoriza) y el IMPORTE nunca sale de la forma
 *  (no se escribe a mano: el de cada periodo es el del recibo, o lo calcula el motor con la tarifa; tampoco el total a pagar). */
/** hoy86 (verificación r2): con qué se estimó un mes de la tabla, en palabras del vendedor. Mientras está sin guardar lo dice la fila;
 *  ya guardado, la marca `sugerido_ia` (antes un PDBT guardado solo decía «Estimado» y el aviso, el historial y el PDF sí decían la IA). */
export function conQueSeEstimo(f: Pick<FilaEditable, 'estimado'>, o: Pick<PeriodoConsumo, 'sugerido_ia'> | null | undefined, tarifa: string | null | undefined): string {
  if (f.estimado) return f.estimado.ia ? 'Estimado por la IA' : 'Estimado con la curva de calor de Sonora';
  if (o?.sugerido_ia) return 'Estimado por la IA';
  return tarifa === 'PDBT' ? 'Estimado' : 'Estimado con la curva de calor de Sonora';
}

export function edicionesDeLectura(medidor: Medidor, original: LecturaRecibo, f: FormaLectura, motivo: string | null): { ediciones: EdicionCampo[]; errores: ErroresLectura; enBlanco: number } {
  const errores: ErroresLectura = {};
  const base = `/lecturas/${medidor}`;
  const ed: EdicionCampo[] = [];

  const tarifa = f.tarifa === '' ? null : f.tarifa;
  if (tarifa !== null && !(TARIFAS as readonly string[]).includes(tarifa)) errores.tarifa = 'Tarifa no válida';
  const facturacion = f.facturacion === '' ? null : f.facturacion;
  const demandaC = num(f.demandaContratada, 'demandaContratada', errores, false);
  const cargaC = num(f.cargaConectada, 'cargaConectada', errores, false);

  const enBlanco = f.filas.filter(filaEnBlanco).length;
  const filas: { fila: FilaEditable; p: PeriodoConsumo | null; kwh: number | null; importe: number | null; demanda: number | null }[] = f.filas.flatMap((fila, i) => {
    if (filaEnBlanco(fila)) return [];
    if (!fila.etiqueta.trim()) errores[`fila.${i}.etiqueta`] = 'Falta';
    if (fila.etiqueta.trim().length > 40) errores[`fila.${i}.etiqueta`] = 'Máximo 40 letras';
    if (fila.inicio && !FECHA.test(fila.inicio)) errores[`fila.${i}.inicio`] = 'Fecha no válida';
    if (fila.fin && !FECHA.test(fila.fin)) errores[`fila.${i}.fin`] = 'Fecha no válida';
    const p = fila.idx === null ? null : (original.historial[fila.idx] ?? null);
    return [
      {
        fila,
        p,
        kwh: num(fila.kwh, `fila.${i}.kwh`, errores, true),
        // z61: el importe es el que ya tenía el periodo (el del recibo); uno nuevo no trae (lo calcula el motor con la tarifa).
        importe: p?.importe ?? null,
        demanda: num(fila.demanda, `fila.${i}.demanda`, errores, false),
      },
    ];
  });
  if (Object.keys(errores).length) return { ediciones: [], errores, enBlanco };

  const campo = (ruta: string, antes: unknown, despues: unknown) => {
    if (!iguales(antes, despues)) ed.push(reemplazo(`${base}${ruta}`, antes, despues, motivo));
  };
  campo('/tarifa', original.tarifa, tarifa);
  campo('/facturacion', original.facturacion, facturacion);
  campo('/demanda_contratada_kw', original.demanda_contratada_kw, demandaC);
  campo('/carga_conectada_kw', original.carga_conectada_kw, cargaC);
  for (const k of Object.keys(f.privados) as (keyof DatosPrivados)[]) {
    const v = f.privados[k].trim();
    campo(`/privados/${k}`, original.privados[k], v === '' ? null : v);
  }

  // z60: lo que puso «Completar el año» va siempre como historial completo (cada renglón con su origen y su ajuste).
  const completado = filas.some((x) => x.fila.estimado || x.fila.redatada);
  const mismaForma = !completado && filas.length === original.historial.length && filas.every((x, i) => x.fila.idx === i);
  if (mismaForma) {
    filas.forEach((x, i) => {
      const p = x.p!;
      const antesFila = ed.length;
      campo(`/historial/${i}/etiqueta`, p.etiqueta, x.fila.etiqueta.trim());
      campo(`/historial/${i}/inicio`, p.inicio, x.fila.inicio || null);
      campo(`/historial/${i}/fin`, p.fin, x.fila.fin || null);
      campo(`/historial/${i}/consumo_kwh`, p.consumo_kwh, x.kwh);
      // hoy86 r4 (vuelta atrás): en una tabla YA GUARDADA el cambio va campo por campo; si el vendedor cambia los kWh que sugirió la
      // IA, ese periodo deja de ser «de la IA» (antes la marca se quedaba y el aviso y el PDF decían «por la IA» de su número).
      if (p.sugerido_ia && x.kwh !== p.consumo_kwh) campo(`/historial/${i}/sugerido_ia`, p.sugerido_ia, null);
      campo(`/historial/${i}/demanda_kw`, p.demanda_kw, x.demanda);
      // hoy83: un mes que estimó «Completar el año» sigue «estimado» aunque se le cambien los kWh (no es un recibo real).
      if (ed.length > antesFila && p.origen !== 'manual' && !esCompletado(p)) campo(`/historial/${i}/origen`, p.origen, 'manual');
    });
  } else {
    const nuevo: PeriodoConsumo[] = filas.map((x) => {
      const cambio =
        !x.p ||
        x.p.consumo_kwh !== x.kwh ||
        x.p.importe !== x.importe ||
        x.p.demanda_kw !== x.demanda ||
        x.p.etiqueta !== x.fila.etiqueta.trim() ||
        (x.p.inicio ?? '') !== x.fila.inicio ||
        (x.p.fin ?? '') !== x.fila.fin;
      // ADENDA z60: un periodo que estimó «Completar el año» va «estimado», sin importe (el pago lo estima el motor con la tarifa).
      // Si era un mes del recibo casi en cero, guarda lo que decía el recibo (ajuste_vendedor). hoy83: aunque el vendedor le
      // cambie los kWh sigue «estimado» (antes quedaba «a mano» y contaba como recibo REAL); si lo regresa al número del recibo,
      // vuelve a ser el renglón del recibo tal cual (con su importe).
      const regresaAlRecibo = !!x.fila.estimado && !!x.p && x.p.origen !== 'estimado' && x.kwh === x.p.consumo_kwh;
      if (regresaAlRecibo) return { ...x.p!, inicio: x.fila.inicio || null, fin: x.fila.fin || null, etiqueta: x.fila.etiqueta.trim() };
      if (x.fila.estimado) {
        const fila: PeriodoConsumo = {
          ...(x.p ?? {}),
          inicio: x.fila.inicio || null,
          fin: x.fila.fin || null,
          etiqueta: x.fila.etiqueta.trim(),
          consumo_kwh: x.kwh!,
          importe: null,
          demanda_kw: x.demanda,
          kwh_base: x.p?.kwh_base ?? null,
          kwh_intermedia: x.p?.kwh_intermedia ?? null,
          kwh_punta: x.p?.kwh_punta ?? null,
          factor_potencia_pct: x.p?.factor_potencia_pct ?? null,
          factor_carga_pct: x.p?.factor_carga_pct ?? null,
          precio_medio: x.p?.precio_medio ?? null,
          origen: 'estimado',
          // hoy86 · z64-2: lo que sugirió la IA queda marcado (el motor y el PDF lo dicen de la IA); si el vendedor le cambió el
          // número, ya no es de la IA.
          ...(x.fila.estimado.ia && x.kwh === x.fila.estimado.kwh ? { sugerido_ia: x.fila.estimado.ia } : x.p?.sugerido_ia ? { sugerido_ia: null } : {}),
        };
        return x.p && x.p.consumo_kwh !== x.kwh && x.p.origen !== 'estimado' ? { ...periodoConConsumoEditado(x.p, fila, { motivo: x.fila.estimado.motivo, fuente: 'vendedor' }), origen: 'estimado', ...(fila.sugerido_ia ? { sugerido_ia: fila.sugerido_ia } : {}) } : fila;
      }
      // z60: el periodo del alta al que solo se le movió el inicio conserva su origen (sus kWh y su importe son del recibo).
      const soloFechas = !!x.fila.redatada && !!x.p && x.p.consumo_kwh === x.kwh && x.p.importe === x.importe && x.p.demanda_kw === x.demanda;
      // z25c: se parte del periodo de antes (...x.p) para no perder lo que la tabla no edita (ajuste_vendedor, cargos,
      // días, bloques); si cambió el consumo, aplicarEdiciones decide qué del recibo viejo ya no vale.
      const fila: PeriodoConsumo = {
        ...x.p,
        inicio: x.fila.inicio || null,
        fin: x.fila.fin || null,
        etiqueta: x.fila.etiqueta.trim(),
        consumo_kwh: x.kwh!,
        importe: x.importe,
        demanda_kw: x.demanda,
        kwh_base: x.p?.kwh_base ?? null,
        kwh_intermedia: x.p?.kwh_intermedia ?? null,
        kwh_punta: x.p?.kwh_punta ?? null,
        factor_potencia_pct: x.p?.factor_potencia_pct ?? null,
        factor_carga_pct: x.p?.factor_carga_pct ?? null,
        precio_medio: x.p?.precio_medio ?? null,
        // hoy83: un mes guardado que estimó «Completar el año» sigue «estimado» aunque le cambien los kWh.
        origen: cambio && !soloFechas ? (x.p && esCompletado(x.p) ? 'estimado' : 'manual') : x.p!.origen,
        // hoy86 · z64-2: un periodo que sugirió la IA y al que el vendedor le cambió los kWh ya no es de la IA.
        ...(x.p?.sugerido_ia && x.kwh !== x.p.consumo_kwh ? { sugerido_ia: null } : {}),
      };
      // hoy85 (z25b, verificador de hoy84): si en el MISMO guardado cambian los kWh y también las fechas o la etiqueta (el alta que el
      // cotizador toma como el periodo completo, un renglón sin fechas que «Poner los meses que faltan» acomoda), el servidor ya no
      // reconocía el periodo y el importe viejo quedaba pegado a los kWh nuevos, sin `ajuste_vendedor`. Se aplica aquí, del lado de
      // la tabla, la misma regla: lo que cobró el recibo por el consumo de antes deja de valer y queda guardado en el ajuste.
      // (Un mes escrito a mano sin recibo y sin importe no tiene «lo que cobró el recibo» que guardar: se queda como está.)
      const delRecibo = !!x.p && (vieneDelRecibo(x.p) || x.p.importe !== null);
      return x.p && delRecibo && x.p.consumo_kwh !== x.kwh ? periodoConConsumoEditado(x.p, fila, { motivo: motivo?.trim() || MOTIVO_EDITADO_A_MANO, fuente: 'vendedor' }) : fila;
    });
    campo('/historial', original.historial, nuevo);
  }
  return { ediciones: ed, errores, enBlanco };
}

/** hoy85 (verificador de hoy84): cuántos cambios hizo el VENDEDOR de `inicial` (lo que el cotizador puso al abrir la tabla: el alta
 *  tomada como el periodo completo, los meses en blanco) a `forma`: cada dato de arriba y cada renglón distinto, agregado o quitado.
 *  Así la barra no dice «1 cambio sin guardar» sin que nadie tocara nada. */
export function cambiosDeLaForma(inicial: FormaLectura, forma: FormaLectura): number {
  let n = 0;
  for (const k of ['tarifa', 'facturacion', 'total', 'demandaContratada', 'cargaConectada'] as const) if (inicial[k] !== forma[k]) n++;
  for (const k of Object.keys(forma.privados) as (keyof FormaLectura['privados'])[]) if (inicial.privados[k] !== forma.privados[k]) n++;
  const clave = (f: FilaEditable) => (f.idx !== null ? `#${f.idx}` : `${f.inicio}|${f.fin}|${f.etiqueta}`);
  const antes = new Map<string, FilaEditable[]>();
  for (const f of inicial.filas) antes.set(clave(f), [...(antes.get(clave(f)) ?? []), f]);
  for (const f of forma.filas) {
    const xs = antes.get(clave(f));
    const a = xs?.shift();
    if (!a || !iguales(a, f)) n++;
  }
  for (const xs of antes.values()) n += xs.length;
  return n;
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Periodo corto para la tabla del historial ("21 jul – 20 ago 25"; si cambia de año, "19 dic 25 – 20 ene 26").
 *  null si alguna fecha no es AAAA-MM-DD. Las etiquetas que imprime CFE ("del 21 JUL 25 al 20 AGO 25") no caben en
 *  una celda de celular. */
export function periodoCorto(inicio: string, fin: string): string | null {
  const f = /^(\d{4})-(\d{2})-(\d{2})$/;
  const a = f.exec(inicio);
  const b = f.exec(fin);
  if (!a || !b) return null;
  const dia = (m: RegExpExecArray) => `${Number(m[3])} ${MESES_CORTOS[Number(m[2]) - 1] ?? '?'}`;
  return a[1] === b[1] ? `${dia(a)} – ${dia(b)} ${b[1]!.slice(2)}` : `${dia(a)} ${a[1]!.slice(2)} – ${dia(b)} ${b[1]!.slice(2)}`;
}

function ultimoDia(anio: number, mes0: number): string {
  const d = new Date(Date.UTC(anio, mes0 + 1, 0));
  return d.toISOString().slice(0, 10);
}

/** Filas vacías para capturar a mano los últimos 12 meses (o 6 bimestres) que terminan el mes anterior a `hoy`. */
export function filasParaCapturar(hoy: string, bimestral: boolean): FilaEditable[] {
  const anio = Number(hoy.slice(0, 4));
  const mes = Number(hoy.slice(5, 7)) - 1;
  const n = bimestral ? 6 : 12;
  const paso = bimestral ? 2 : 1;
  const filas: FilaEditable[] = [];
  for (let i = n; i >= 1; i--) {
    const finAbs = anio * 12 + mes - 1 - (i - 1) * paso;
    const iniAbs = finAbs - paso + 1;
    const ai = Math.floor(iniAbs / 12);
    const mi = iniAbs % 12;
    const af = Math.floor(finAbs / 12);
    const mf = finAbs % 12;
    filas.push({
      idx: null,
      etiqueta: bimestral ? `${MESES[mi]}-${MESES[mf]} ${String(af).slice(2)}` : `${MESES[mf]} ${String(af).slice(2)}`,
      inicio: `${ai}-${String(mi + 1).padStart(2, '0')}-01`,
      fin: ultimoDia(af, mf),
      kwh: '',
      importe: '',
      demanda: '',
    });
  }
  return filas;
}

/** Lectura nueva capturada a mano (sin IA). Las filas sin kWh no entran. ADENDA z61: solo se capturan los kWh; el importe de cada
 *  periodo (y el total) lo calcula el cotizador con la tarifa: nunca se manda uno escrito a mano. */
export function lecturaManual(medidor: Medidor, archivoIds: string[], f: FormaLectura): { lectura: LecturaRecibo | null; errores: ErroresLectura } {
  const errores: ErroresLectura = {};
  const historial: PeriodoConsumo[] = [];
  f.filas.forEach((fila, i) => {
    if (!fila.kwh.trim()) return;
    const kwh = num(fila.kwh, `fila.${i}.kwh`, errores, true);
    const demanda = num(fila.demanda, `fila.${i}.demanda`, errores, false);
    if (kwh === null) return;
    historial.push({
      inicio: fila.inicio || null,
      fin: fila.fin || null,
      etiqueta: fila.etiqueta.trim() || `Periodo ${i + 1}`,
      consumo_kwh: kwh,
      importe: null,
      demanda_kw: demanda,
      kwh_base: null,
      kwh_intermedia: null,
      kwh_punta: null,
      factor_potencia_pct: null,
      factor_carga_pct: null,
      precio_medio: null,
      origen: 'manual',
    });
  });
  if (!f.tarifa) errores.tarifa = 'Elige la tarifa que dice el recibo';
  if (!f.facturacion) errores.facturacion = 'Elige mensual o bimestral';
  if (historial.length === 0) errores.filas = 'Escribe al menos el consumo de un periodo';
  if (Object.keys(errores).length) return { lectura: null, errores };
  const completos = historial.length >= (f.facturacion === 'bimestral' ? 6 : 12);
  const ultimo = historial[historial.length - 1]!;
  return {
    lectura: {
      medidor,
      archivo_ids: archivoIds,
      estado: completos ? 'leido' : 'parcial',
      privados: { titular: null, numero_servicio: null, direccion: null, rmu: null, cuenta: null, numero_medidor: null },
      tarifa: f.tarifa as Tarifa,
      facturacion: f.facturacion as Facturacion,
      periodo_facturado: ultimo.inicio && ultimo.fin ? { inicio: ultimo.inicio, fin: ultimo.fin } : null,
      total_a_pagar: null,
      demanda_contratada_kw: null,
      carga_conectada_kw: null,
      multiplicador: null,
      historial,
      cargos_ultimo_periodo: null,
      precios_energia: [],
      campos_dudosos: [],
      notas_ia: null,
    },
    errores,
  };
}

/** Pedido del vendedor (21-oct, SX-2026-0014): el pago de un periodo cuyos kWh cambió el vendedor o la IA NO se escribe:
 *  es el que estima el MOTOR con la tarifa (el mismo número del cálculo: `pago_hoy` del resultado guardado, una sola fuente).
 *  - «recibo»: lo que cobró CFE (o lo que capturó el vendedor), editable como siempre.
 *  - «estimado»: el motor lo estimó con la tarifa (kWh ajustados, o importe viejo que no corresponde a sus kWh): se ve el
 *    monto, sin campo.
 *  - «al_guardar»: los kWh en pantalla todavía no se guardan (o el cálculo no está al día con ellos): el motor lo calcula
 *    al guardar. Solo se compara contra un periodo del resultado con las MISMAS fechas y los MISMOS kWh guardados: si el
 *    resultado es de antes, se dice «al guardar» en vez de enseñar un monto de otro consumo. */
export type PagoDeFila = { tipo: 'recibo' } | { tipo: 'estimado'; monto: number; viejo: boolean } | { tipo: 'al_guardar' };
export function pagoDeFila(
  fila: FilaEditable,
  o: PeriodoConsumo | null,
  periodos: readonly Pick<PeriodoCalculo, 'inicio' | 'fin' | 'consumo_kwh' | 'pago_hoy' | 'kwh_recibo' | 'pago_recibo'>[] | null,
  desdeRecibo: boolean,
): PagoDeFila {
  // z60/z61: una fila nueva (sugerida, o con los kWh que escribió el vendedor) todavía sin guardar: su pago lo calcula el motor al
  // guardar con la tarifa (nunca se escribe). En blanco, todavía no hay pago (se ve «—»).
  if (!o) return fila.estimado || numeroDe(fila.kwh) !== null ? { tipo: 'al_guardar' } : { tipo: 'recibo' };
  const escrito = fila.kwh.replace(/,/g, '').trim();
  const cambio = escrito !== '' && Number(escrito) !== o.consumo_kwh;
  // ADENDA z52 (candado D7): con base futura (negocio en PDBT o aumento de consumo) el periodo del cálculo trae aparte lo del
  // RECIBO (kwh_recibo, pago_recibo); esta columna es la del recibo: nunca enseña el «pagaría sin paneles» del cálculo.
  // z60: por su corte y sus kWh (el primer periodo del alta, más corto, el motor lo toma como el periodo completo: otro inicio)
  const p = periodos?.find((q) => q.fin === o.fin && (q.inicio === o.inicio || (!!o.inicio && q.inicio < o.inicio)) && Math.abs((q.kwh_recibo ?? q.consumo_kwh) - o.consumo_kwh) < 0.01) ?? null;
  const delRecibo = p ? (p.pago_recibo ?? p.pago_hoy) : null;
  // Importe viejo (kWh cambiados antes del 20-oct con el importe del recibo): el motor no lo usó (punto 2).
  const viejo = desdeRecibo && !o.ajuste_vendedor && o.origen === 'manual' && o.importe !== null && delRecibo !== null && Math.abs(delRecibo - o.importe) > 0.01;
  // z60: un periodo estimado por «Completar el año» (origen «estimado», sin IA) tampoco trae lo que cobró CFE: su pago es del motor.
  // z60 (caso de Miguel): un periodo escrito a mano SIN importe en una tabla que vino del recibo (agregado con «Agregar periodo»):
  // su pago también lo estima el motor; se ve con su monto (antes salía «—» y el Total de la tabla no lo sumaba).
  const ajustado = (!!o.ajuste_vendedor || esCompletado(o) || (desdeRecibo && o.origen === 'manual')) && o.importe === null;
  if (!ajustado && !viejo && !(desdeRecibo && cambio)) return { tipo: 'recibo' };
  if (cambio || delRecibo === null) return { tipo: 'al_guardar' };
  return { tipo: 'estimado', monto: delRecibo, viejo };
}

/** ADENDA z60: ¿este periodo guardado lo estimó «Completar el año»? (origen «estimado» que no proyectó la IA). */
export const esCompletado = (o: Pick<PeriodoConsumo, 'origen' | 'proyeccion_ia'>): boolean => o.origen === 'estimado' && !o.proyeccion_ia;

/** ADENDA z60: ¿la fila de la tabla es un periodo ESTIMADO por «Completar el año»? (recién puesto, o guardado así). hoy83: aunque
 *  el vendedor le escriba otro número sigue estimado (antes pasaba a «a mano» y contaba como recibo real); deja de serlo solo si
 *  un mes del recibo vuelve al número que decía el recibo. La usan la marca de la tabla y el resumen. */
export function filaEstimada(f: FilaEditable, o: PeriodoConsumo | null): boolean {
  const escritos = f.kwh.replace(/,/g, '').trim();
  if (f.estimado) return !(o && !esCompletado(o) && escritos === String(o.consumo_kwh));
  if (!o || !esCompletado(o)) return false;
  return !(o.ajuste_vendedor && escritos === String(o.ajuste_vendedor.consumo_original_kwh));
}

/** ADENDA z62(1) (Diego, 29-oct): ¿la fila es un mes SIN recibo cuyos kWh escribió el vendedor? (guardada «a mano» sin recibo, o
 *  una fila que puso el cotizador en blanco y ya trae kWh tecleados). En una tabla que vino de recibos, el PDF la imprime como
 *  ESTIMADO (solo los meses con recibo son reales) y el resumen la cuenta con los estimados. Lo sugerido («Sugerir con IA») ya es
 *  `filaEstimada`. */
export function filaSinRecibo(f: FilaEditable, o: PeriodoConsumo | null, lectura: LecturaRecibo | null): boolean {
  if (!lectura || !lecturaDeRecibos(lectura) || filaEstimada(f, o)) return false;
  if (o) return escritoSinRecibo(o, lectura);
  return f.idx === null && !f.estimado && numeroDe(f.kwh) !== null;
}

/** ADENDA z52: lo que el CÁLCULO usa de un renglón del recibo cuando no es el recibo tal cual (base futura): los kWh con el
 *  aumento y lo que pagaría sin paneles en la tarifa de cálculo. null = el cálculo es el recibo (o el renglón no cuenta). */
export function calculoDeFila(o: PeriodoConsumo | null, periodos: readonly Pick<PeriodoCalculo, 'inicio' | 'fin' | 'consumo_kwh' | 'pago_hoy' | 'kwh_recibo' | 'pago_recibo'>[] | null): { kwh: number; pago: number } | null {
  if (!o || !periodos) return null;
  const p = periodos.find((q) => q.inicio === o.inicio && q.fin === o.fin && q.kwh_recibo !== undefined && Math.abs(q.kwh_recibo - o.consumo_kwh) < 0.01);
  return p ? { kwh: p.consumo_kwh, pago: p.pago_hoy } : null;
}

/** z25 (Diego, 20-oct) y pedido del vendedor (21-oct): el cuadro «Pídele a la IA que corrija» de Revisión también sirve para
 *  DESCRIBIR la casa o el negocio y que la IA estime los consumos de los meses que no son reales (casa en construcción). */
export const PISTA_CORREGIR_IA =
  'Ej. "el consumo de julio es 3,800", o describe la casa o el negocio para que la IA estime los consumos: "Desde junio ya viven ahí: 2 pisos, 5 recámaras con minisplit (15 t), estufa y boiler eléctricos, calentones en invierno; estima los meses de antes"';
export const AYUDA_CORREGIR_IA = 'La IA propone; tú decides qué se aplica. Si los meses viejos no son el consumo real, dile qué equipos tiene y cómo se usa: estima esos meses.';
